import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Briefcase, ChevronRight, FileSignature, FileText, FolderOpen, Paperclip, Plus, Receipt, Send, Shield, X,
} from 'lucide-react'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog'
import {
  chatsService,
  documentsService,
  applicationsService,
  uploadService,
} from '../../services/api'
import ChatMessageBubble from './ChatMessageBubble'
import ApplicationStatusBadge from './ApplicationStatusBadge'
import { ContractFormDialog, QuoteFormDialog } from './ChatDocumentModals'
import ChatDocumentsList from './ChatDocumentsList'
import DocumentPreviewModal from './DocumentPreviewModal'
import {
  buildAttachmentMessageText,
  buildDocumentPayloadSummary,
  dedupeDocuments,
  extractCvEntry,
  getDocumentViewUrl,
  getMessagingContextTheme,
  getMyRoleInChat,
  getOtherParticipant,
  parseDocumentReference,
  validateDocFile,
} from './chat-utils'

const extractList = (res) => {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res?.items)) return res.items
  return []
}

const getPostLink = (post) => {
  if (!post?.id) return null
  if (post.type === 'JOB') return `/emplois/${post.id}`
  if (post.type === 'REQUEST') return `/demandes/${post.id}`
  return null
}

const EMPTY_CONTRACT = { title: '', description: '', amount: '', duration: '' }
const EMPTY_QUOTE = { title: '', items: [{ label: '', amount: '' }], total: '', validUntil: '' }

export default function ChatView({
  chat,
  messages,
  currentUserId,
  isPlatformAdmin = false,
  onMessageSent,
  onRefresh,
  messagesEndRef,
  messagingContext = 'particulier',
}) {
  const [inputText, setInputText] = useState('')
  const [sending, setSending] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const [showAdminModal, setShowAdminModal] = useState(false)
  const [pendingStatusAction, setPendingStatusAction] = useState(null)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [documents, setDocuments] = useState([])
  const [showContractForm, setShowContractForm] = useState(false)
  const [showQuoteForm, setShowQuoteForm] = useState(false)
  const [quoteFormMode, setQuoteFormMode] = useState('QUOTE')
  const [contractForm, setContractForm] = useState(EMPTY_CONTRACT)
  const [quoteForm, setQuoteForm] = useState(EMPTY_QUOTE)
  const [contractInputMode, setContractInputMode] = useState('structured')
  const [quoteInputMode, setQuoteInputMode] = useState('structured')
  const [contractFile, setContractFile] = useState(null)
  const [quoteFile, setQuoteFile] = useState(null)
  const [isUploadingDocFile, setIsUploadingDocFile] = useState(false)
  const [isCreatingDocument, setIsCreatingDocument] = useState(false)
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  const [showFilesPanel, setShowFilesPanel] = useState(false)
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const attachmentInputRef = useRef(null)
  const isProContext = messagingContext === 'pro'

  const otherParticipant = getOtherParticipant(chat, currentUserId)
  const recipientUserId = otherParticipant?.userId ?? null
  const myRoleInChat = getMyRoleInChat(chat, currentUserId)
  const isPostAuthorInChat = myRoleInChat === 'AUTHOR'
  const currentApplication = chat?.application
  const canModerateApplication =
    !!currentApplication &&
    isPostAuthorInChat &&
    currentApplication.status === 'IN_DISCUSSION'
  const shouldShowApplicationStatus =
    !!currentApplication && currentApplication.status !== 'IN_DISCUSSION'
  const isChatLocked = currentApplication?.status === 'REJECTED'
  const candidateName = otherParticipant?.user?.name ?? otherParticipant?.name
  const cvEntry = useMemo(
    () => extractCvEntry(messages, candidateName),
    [messages, candidateName]
  )
  const filesCount = documents.length + (cvEntry ? 1 : 0)
  const adminHelpStatus = chat?.adminHelpStatus ?? 'NONE'
  const peerLastReadAt = otherParticipant?.lastReadAt ?? null

  const documentsById = useMemo(() => {
    const map = {}
    for (const doc of documents) {
      map[doc.id] = doc
    }
    return map
  }, [documents])

  const loadDocuments = useCallback(async () => {
    if (!chat?.id) return
    setDocumentsLoading(true)
    try {
      const res = await documentsService.getDocuments({
        chatId: chat.id,
        role: 'all',
        limit: 50,
      })
      setDocuments(dedupeDocuments(extractList(res)))
    } catch (error) {
      console.warn('[ChatView] documents:', error)
    } finally {
      setDocumentsLoading(false)
    }
  }, [chat?.id])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  const openPreview = useCallback((title, url, payloadSummary = null) => {
    setPreview({ title, url, payloadSummary })
  }, [])

  const handleViewCv = useCallback(() => {
    if (!cvEntry?.url) return
    openPreview(cvEntry.label, cvEntry.url)
  }, [cvEntry, openPreview])

  const handleViewDocument = useCallback(async (docOrId) => {
    try {
      let doc = docOrId
      if (typeof docOrId === 'string') {
        const res = await documentsService.getDocumentById(docOrId)
        doc = res?.data ?? res
      }
      if (!doc) return

      const url = getDocumentViewUrl(doc)
      const payloadSummary = buildDocumentPayloadSummary(doc)
      openPreview(doc.title, url, url ? null : payloadSummary)
    } catch {
      toast.error('Impossible d\'ouvrir le document')
    }
  }, [openPreview])

  const handleSendAttachment = async (file) => {
    if (!file || !chat?.id) return

    const fileError = validateDocFile(file)
    if (fileError) {
      toast.error(fileError)
      return
    }

    setIsUploadingAttachment(true)
    try {
      const uploadRes = await uploadService.uploadFile(file)
      const fileUrl = uploadRes?.data?.url ?? uploadRes?.url
      if (!fileUrl) throw new Error('URL du fichier introuvable')

      const res = await chatsService.sendMessage(
        chat.id,
        buildAttachmentMessageText(file.name, fileUrl)
      )
      const msg = res?.data ?? res
      onMessageSent?.(msg)
      onRefresh?.()
      toast.success('Pièce jointe envoyée')
    } catch (error) {
      toast.error(error?.message ?? 'Impossible d\'envoyer la pièce jointe')
    } finally {
      setIsUploadingAttachment(false)
      if (attachmentInputRef.current) attachmentInputRef.current.value = ''
    }
  }

  const handleAttachmentPick = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    await handleSendAttachment(file)
  }

  const handleAttachClick = () => {
    if (isProContext) {
      setShowAttachMenu(!showAttachMenu)
      return
    }
    attachmentInputRef.current?.click()
  }

  const handleSend = async (e) => {
    e?.preventDefault?.()
    if (!inputText.trim() || sending || !chat?.id) return

    setSending(true)
    try {
      const res = await chatsService.sendMessage(chat.id, inputText.trim())
      const msg = res?.data ?? res
      setInputText('')
      onMessageSent?.(msg)
      onRefresh?.()
    } catch (error) {
      toast.error(error?.message ?? 'Erreur lors de l\'envoi du message')
    } finally {
      setSending(false)
    }
  }

  const resetContractForm = () => {
    setContractForm(EMPTY_CONTRACT)
    setContractFile(null)
    setContractInputMode('structured')
    setShowContractForm(false)
  }

  const resetQuoteForm = () => {
    setQuoteForm(EMPTY_QUOTE)
    setQuoteFile(null)
    setQuoteInputMode('structured')
    setShowQuoteForm(false)
  }

  const handleSendContract = async () => {
    if (!isProContext) {
      toast.info('Les contrats sont réservés à l\'espace professionnel')
      return
    }
    if (!chat?.id || !recipientUserId) {
      toast.error('Destinataire introuvable pour ce chat')
      return
    }

    if (contractInputMode === 'file') {
      const title = contractForm.title.trim()
      if (title.length < 3 || !contractFile) return
      setIsUploadingDocFile(true)
      try {
        const uploadRes = await uploadService.uploadFile(contractFile)
        const fileUrl = uploadRes?.data?.url ?? uploadRes?.url
        if (!fileUrl) throw new Error('URL du fichier introuvable')
        await documentsService.uploadDocumentWithFile({
          type: 'CONTRACT',
          chatId: chat.id,
          recipientUserId,
          title,
          fileUrl,
          originalName: contractFile.name,
        })
        resetContractForm()
        await loadDocuments()
        onRefresh?.()
        toast.success('Contrat envoyé')
      } catch {
        toast.error('Impossible d\'envoyer le contrat')
      } finally {
        setIsUploadingDocFile(false)
      }
      return
    }

    const amount = Number(contractForm.amount)
    const normalizedDescription = contractForm.description.trim()
    if (!contractForm.title.trim() || !Number.isFinite(amount) || amount < 0) return
    if (normalizedDescription.length > 0 && normalizedDescription.length < 10) {
      toast.error('La description du contrat doit contenir au moins 10 caractères')
      return
    }

    setIsCreatingDocument(true)
    try {
      await documentsService.createContract(
        chat.id,
        recipientUserId,
        contractForm.title.trim(),
        {
          description: normalizedDescription || 'Description à définir',
          amount,
          duration: contractForm.duration.trim() || 'À définir',
        }
      )
      resetContractForm()
      await loadDocuments()
      onRefresh?.()
      toast.success('Contrat envoyé')
    } catch {
      toast.error('Impossible d\'envoyer le contrat')
    } finally {
      setIsCreatingDocument(false)
    }
  }

  const handleSendQuote = async () => {
    if (!isProContext) {
      toast.info('Les devis et factures sont réservés à l\'espace professionnel')
      return
    }
    if (!chat?.id || !recipientUserId) {
      toast.error('Destinataire introuvable pour ce chat')
      return
    }

    if (quoteInputMode === 'file') {
      const title = quoteForm.title.trim()
      if (title.length < 3 || !quoteFile) return
      setIsUploadingDocFile(true)
      try {
        const uploadRes = await uploadService.uploadFile(quoteFile)
        const fileUrl = uploadRes?.data?.url ?? uploadRes?.url
        if (!fileUrl) throw new Error('URL du fichier introuvable')
        await documentsService.uploadDocumentWithFile({
          type: quoteFormMode,
          chatId: chat.id,
          recipientUserId,
          title,
          fileUrl,
          originalName: quoteFile.name,
        })
        resetQuoteForm()
        await loadDocuments()
        onRefresh?.()
        toast.success('Document envoyé')
      } catch {
        toast.error('Impossible d\'envoyer ce document')
      } finally {
        setIsUploadingDocFile(false)
      }
      return
    }

    const validItems = quoteForm.items.filter((item) => item.label.trim() && item.amount.trim())
    if (!quoteForm.title.trim() || !quoteForm.total.trim() || validItems.length === 0) return
    const total = Number(quoteForm.total)
    if (!Number.isFinite(total) || total < 0) return
    const items = validItems
      .map((item) => ({ label: item.label.trim(), amount: Number(item.amount) }))
      .filter((item) => Number.isFinite(item.amount) && item.amount >= 0)
    if (!items.length) return

    setIsCreatingDocument(true)
    try {
      if (quoteFormMode === 'QUOTE') {
        await documentsService.createQuote(chat.id, recipientUserId, quoteForm.title.trim(), {
          items,
          total,
          validUntil: quoteForm.validUntil.trim() || undefined,
        })
      } else {
        await documentsService.createInvoice(chat.id, recipientUserId, quoteForm.title.trim(), {
          items,
          total,
          dueDate: quoteForm.validUntil.trim() || undefined,
        })
      }
      resetQuoteForm()
      await loadDocuments()
      onRefresh?.()
      toast.success('Document envoyé')
    } catch {
      toast.error('Impossible d\'envoyer ce document')
    } finally {
      setIsCreatingDocument(false)
    }
  }

  const confirmRequestAdmin = async () => {
    try {
      await chatsService.requestAdminHelp(chat.id)
      setShowAdminModal(false)
      toast.success('Demande envoyée à un administrateur')
      onRefresh?.()
    } catch {
      toast.error('Impossible de demander un administrateur')
    }
  }

  const confirmStatusAction = async () => {
    if (!currentApplication || !pendingStatusAction || pendingStatusAction === 'CLOSED') return

    setIsUpdatingStatus(true)
    try {
      await applicationsService.updateApplicationStatus(currentApplication.id, pendingStatusAction)
      setPendingStatusAction(null)
      toast.success('Statut de la candidature mis à jour')
      onRefresh?.()
    } catch {
      toast.error('Impossible de modifier le statut de la candidature')
      setPendingStatusAction(null)
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const addQuoteItem = () => {
    setQuoteForm((prev) => ({ ...prev, items: [...prev.items, { label: '', amount: '' }] }))
  }

  const updateQuoteItem = (index, field, value) => {
    setQuoteForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }))
  }

  const removeQuoteItem = (index) => {
    setQuoteForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }))
  }

  const showAdminRequest = adminHelpStatus === 'NONE' && !isPlatformAdmin
  const docBusy = isCreatingDocument || isUploadingDocFile
  const contextTheme = getMessagingContextTheme(messagingContext)

  return (
    <div className="flex flex-1 min-h-0">
      <div className={`flex flex-col flex-1 min-h-0 border-t-4 ${contextTheme.panelAccentClass}`}>
      <div className={`mx-4 md:mx-6 mt-3 flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-xs shrink-0 ${contextTheme.bannerClass}`}>
        <Shield className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
        <div>
          <p className="font-semibold">{contextTheme.securityTitle}</p>
          <p className="mt-0.5 opacity-80 leading-relaxed">{contextTheme.securityText}</p>
        </div>
        <span className={`ml-auto shrink-0 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${contextTheme.badgeClass}`}>
          {contextTheme.shortLabel}
        </span>
      </div>

      <div className="px-4 md:px-6 py-3 bg-white border-b border-slate-200 shrink-0">
        <button
          type="button"
          onClick={() => setShowFilesPanel((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${
            showFilesPanel
              ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]'
              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
          }`}
        >
          <FolderOpen className="w-4 h-4" />
          Fichiers
          {filesCount > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              showFilesPanel ? 'bg-white/20 text-white' : 'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]'
            }`}>
              {filesCount}
            </span>
          )}
        </button>
      </div>

      <div className={`flex-1 overflow-y-auto p-4 md:p-6 min-h-0 ${contextTheme.messageAreaClass}`}>
        {chat?.post && (
          <div className="mb-6 bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.08)] flex items-center justify-center shrink-0">
                {chat.post.type === 'JOB' ? (
                  <Briefcase className="w-5 h-5 text-[hsl(var(--primary))]" />
                ) : (
                  <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900">{chat.post.title}</p>
                <p className="text-sm text-slate-500">
                  {chat.post.locationCity}
                  {chat.post.category?.name ? ` · ${chat.post.category.name}` : ''}
                </p>
              </div>
              {getPostLink(chat.post) && (
                <Link
                  to={getPostLink(chat.post)}
                  className="text-sm text-[hsl(var(--primary))] hover:underline flex items-center gap-1 shrink-0"
                >
                  Voir <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        )}

        {adminHelpStatus === 'JOINED' && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
            <Shield className="w-4 h-4 shrink-0" />
            Un administrateur a rejoint la conversation
          </div>
        )}

        {messages.map((msg, index) => {
          const reference = parseDocumentReference(msg.text)
          return (
            <ChatMessageBubble
              key={msg.id}
              message={msg}
              currentUserId={currentUserId}
              index={index}
              peerLastReadAt={peerLastReadAt}
              linkedDocument={reference ? documentsById[reference.documentId] ?? null : null}
              onOpenDocument={handleViewDocument}
              messagingContext={messagingContext}
            />
          )
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className={contextTheme.composerClass}>
        {canModerateApplication && (
          <div className="px-4 pt-4 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-900 mb-3">Décision sur la candidature</p>
            {cvEntry && (
              <button
                type="button"
                onClick={handleViewCv}
                className="w-full flex items-center gap-2 px-4 py-3 mb-3 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.15)] transition-colors"
              >
                <FolderOpen className="w-5 h-5 shrink-0" />
                <span className="font-medium">{cvEntry.label}</span>
              </button>
            )}
            <div className="flex gap-3 mb-4">
              <Button
                type="button"
                onClick={() => setPendingStatusAction('ACCEPTED')}
                disabled={isUpdatingStatus}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                Accepter
              </Button>
              <Button
                type="button"
                onClick={() => setPendingStatusAction('REJECTED')}
                disabled={isUpdatingStatus}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Refuser
              </Button>
            </div>
          </div>
        )}

        {!canModerateApplication && isPostAuthorInChat && cvEntry && (
          <div className="px-4 pt-4">
            <button
              type="button"
              onClick={handleViewCv}
              className="w-full flex items-center gap-2 px-4 py-3 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.15)] transition-colors"
            >
              <FolderOpen className="w-5 h-5 shrink-0" />
              <span className="font-medium">{cvEntry.label}</span>
            </button>
          </div>
        )}

        {shouldShowApplicationStatus && currentApplication && (
          <div className="px-4 pt-4 flex items-center gap-3">
            <span className="text-sm text-slate-500">Statut de la candidature</span>
            <ApplicationStatusBadge status={currentApplication.status} />
          </div>
        )}

        {showAdminRequest && (
          <div className="px-4 pt-3">
            <button
              type="button"
              onClick={() => setShowAdminModal(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.15)] transition-colors text-sm font-medium"
            >
              <Shield className="w-4 h-4" />
              Demander un Admin
            </button>
          </div>
        )}

        {isChatLocked && (
          <div className="px-4 pt-3">
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="font-semibold text-red-700 text-sm">Discussion clôturée</p>
              <p className="text-sm text-slate-600 mt-1">
                La candidature a été refusée. Plus aucun message ne peut être envoyé.
              </p>
            </div>
          </div>
        )}

        {!isChatLocked && isProContext && showAttachMenu && (
          <div className="px-4 pt-3 grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => { setShowAttachMenu(false); setShowContractForm(true) }}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-[hsl(var(--primary)/0.08)] hover:bg-[hsl(var(--primary)/0.15)] transition-colors"
            >
              <FileSignature className="w-6 h-6 text-[hsl(var(--primary))]" />
              <span className="text-xs font-semibold text-slate-900">Contrat</span>
            </button>
            <button
              type="button"
              onClick={() => { setShowAttachMenu(false); setQuoteFormMode('QUOTE'); setShowQuoteForm(true) }}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors"
            >
              <Receipt className="w-6 h-6 text-emerald-600" />
              <span className="text-xs font-semibold text-slate-900">Devis</span>
            </button>
            <button
              type="button"
              onClick={() => { setShowAttachMenu(false); setQuoteFormMode('INVOICE'); setShowQuoteForm(true) }}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors"
            >
              <Receipt className="w-6 h-6 text-amber-500" />
              <span className="text-xs font-semibold text-slate-900">Facture</span>
            </button>
          </div>
        )}

        {!isChatLocked && (
          <form onSubmit={handleSend} className="p-4 flex items-end gap-3">
            <input
              ref={attachmentInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              className="hidden"
              onChange={handleAttachmentPick}
              aria-hidden
            />
            <button
              type="button"
              onClick={handleAttachClick}
              disabled={isUploadingAttachment}
              className={`h-11 w-11 shrink-0 rounded-xl border-2 flex items-center justify-center transition-colors ${
                isProContext && showAttachMenu
                  ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white'
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300'
              }`}
              aria-label={isProContext ? 'Joindre un document professionnel' : 'Joindre une pièce jointe'}
            >
              {isUploadingAttachment ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-slate-600" />
              ) : isProContext ? (
                <Plus className={`w-5 h-5 transition-transform ${showAttachMenu ? 'rotate-45' : ''}`} />
              ) : (
                <Paperclip className="w-5 h-5" />
              )}
            </button>
            <div className={`flex-1 rounded-xl transition-all ${contextTheme.inputWrapperClass}`}>
              <Textarea
                placeholder={contextTheme.isPro ? 'Votre message professionnel…' : 'Votre message…'}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend(e)
                  }
                }}
                className="min-h-[44px] max-h-32 resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 px-4 py-3"
                rows={1}
              />
            </div>
            <Button
              type="submit"
              disabled={!inputText.trim() || sending || isUploadingAttachment}
              className={`h-11 w-11 p-0 shrink-0 ${contextTheme.isPro ? 'bg-amber-600 hover:bg-amber-700' : 'btn-clickpro-primary'}`}
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </form>
        )}
      </div>
      </div>

      {showFilesPanel && (
        <aside className="hidden md:flex w-80 lg:w-96 flex-col bg-white border-l border-slate-200 shrink-0 min-h-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
            <span className="font-semibold text-slate-900">Fichiers</span>
            <button
              type="button"
              onClick={() => setShowFilesPanel(false)}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
              aria-label="Fermer le panneau fichiers"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <ChatDocumentsList
            cvEntry={cvEntry}
            documents={documents}
            currentUserId={currentUserId}
            onViewCv={handleViewCv}
            onViewDocument={handleViewDocument}
            loading={documentsLoading}
          />
        </aside>
      )}

      {showFilesPanel && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
            <span className="font-semibold text-slate-900">Fichiers de la conversation</span>
            <button
              type="button"
              onClick={() => setShowFilesPanel(false)}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <ChatDocumentsList
              cvEntry={cvEntry}
              documents={documents}
              currentUserId={currentUserId}
              onViewCv={() => {
                handleViewCv()
                setShowFilesPanel(false)
              }}
              onViewDocument={(doc) => {
                handleViewDocument(doc)
                setShowFilesPanel(false)
              }}
              loading={documentsLoading}
            />
          </div>
        </div>
      )}

      <DocumentPreviewModal
        open={!!preview}
        onOpenChange={(open) => { if (!open) setPreview(null) }}
        title={preview?.title}
        url={preview?.url}
        payloadSummary={preview?.payloadSummary}
      />

      <Dialog open={showAdminModal} onOpenChange={setShowAdminModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[hsl(var(--primary))]" />
              Demander un administrateur
            </DialogTitle>
            <DialogDescription>
              Un administrateur pourra rejoindre cette conversation pour vous aider à résoudre un éventuel litige.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <Button type="button" onClick={confirmRequestAdmin} className="btn-clickpro-primary">
              Confirmer
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowAdminModal(false)}>
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingStatusAction === 'ACCEPTED' || pendingStatusAction === 'REJECTED'}
        onOpenChange={(open) => { if (!open && !isUpdatingStatus) setPendingStatusAction(null) }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingStatusAction === 'ACCEPTED' ? 'Accepter la candidature' : 'Refuser la candidature'}
            </DialogTitle>
            <DialogDescription>
              {pendingStatusAction === 'ACCEPTED'
                ? 'Le candidat sera notifié immédiatement de votre décision.'
                : 'Le candidat sera informé que sa candidature n\'a pas été retenue.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <Button
              type="button"
              onClick={confirmStatusAction}
              disabled={isUpdatingStatus}
              className={pendingStatusAction === 'ACCEPTED' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {pendingStatusAction === 'ACCEPTED' ? 'Confirmer l\'acceptation' : 'Confirmer le refus'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPendingStatusAction(null)}
              disabled={isUpdatingStatus}
            >
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ContractFormDialog
        open={showContractForm}
        onOpenChange={(open) => { if (!open) resetContractForm(); else setShowContractForm(true) }}
        form={contractForm}
        onFormChange={setContractForm}
        inputMode={contractInputMode}
        onInputModeChange={setContractInputMode}
        file={contractFile}
        onFileChange={setContractFile}
        onSubmit={handleSendContract}
        isBusy={docBusy}
      />

      <QuoteFormDialog
        open={showQuoteForm}
        onOpenChange={(open) => { if (!open) resetQuoteForm(); else setShowQuoteForm(true) }}
        mode={quoteFormMode}
        form={quoteForm}
        onFormChange={setQuoteForm}
        inputMode={quoteInputMode}
        onInputModeChange={setQuoteInputMode}
        file={quoteFile}
        onFileChange={setQuoteFile}
        onSubmit={handleSendQuote}
        isBusy={docBusy}
        onAddItem={addQuoteItem}
        onUpdateItem={updateQuoteItem}
        onRemoveItem={removeQuoteItem}
      />
    </div>
  )
}
